<template>
	<div id="clientStatic" class="jc-view">
		<JuniperPageHeader :back="'/juniper/clients/edit/' + client.id" :title="client ? client.name : ''" subtitle="Static Generation">
			<button @click="downloadStatic(backups[0].name)" class="j-action-btn" :disabled="backups.length === 0" title="Download latest static package">
				<font-awesome-icon :icon="['fas', 'file-csv']" class="me-1" />
				<span>Download Files</span>
			</button>
		</JuniperPageHeader>

		<div class="row g-3 mb-3">
			<div class="col-md-4">
				<div class="js-stat">
					<div class="js-stat__label">Last Generated</div>
					<div class="js-stat__value" v-if="backups.length > 0">{{ backups[0].time }}</div>
					<div class="js-stat__value" v-else>Not available</div>
					<div class="js-stat__sub" v-if="backups.length > 0">{{ backups[0].date }} UTC</div>
				</div>
			</div>
			<div class="col-md-4">
				<div class="js-stat">
					<div class="js-stat__label">Build Structure</div>
					<div class="js-stat__value">{{ total_files }} files</div>
					<div class="js-stat__sub">{{ total_directories }} directories</div>
				</div>
			</div>
			<div class="col-md-4">
				<div class="js-stat">
					<div class="js-stat__label">Total Size</div>
					<div class="js-stat__value">{{ total_size }} MB</div>
					<div class="js-stat__sub">Current latest backup package</div>
				</div>
			</div>
		</div>

		<div class="row g-3">
			<div class="col-lg-12">
				<div class="js-panel h-100">
					<div class="js-panel__title">Quick Restore</div>
					<div class="js-panel__subtitle">Revert to a recent static build if needed.</div>
					<div v-if="backups.length > 0" class="table-responsive mt-3">
						<table class="table table-hover align-middle mb-0">
							<tbody>
								<tr v-for="(backup, i) in backups" :key="i">
									<td>
										<div class="fw-semibold">{{ backup.time }}</div>
										<small class="text-muted">{{ backup.date }}</small>
									</td>
									<td class="text-muted">{{ backup.size }}</td>
									<td class="text-end">
										<button @click="applayBackup(backup, '')" class="btn btn-primary btn-sm">
											<template v-if="backup.name === restore">
												<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
											</template>
											<template v-else>Restore</template>
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
					<div v-else class="js-empty">No backups available for this client.</div>
					<div v-if="backups.length > 2" class="mt-3">
						<button data-bs-toggle="modal" @click="getAllBackups()" data-bs-target="#backups" role="button" class="btn btn-outline-primary btn-sm">
							View All Backups
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal for backups -->
		<div class="modal fade" id="backups" tabindex="-1" aria-labelledby="lofInfoLabel" aria-hidden="true">
			<div class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header justify-content-start">
						<div>
							<h5 class="modal-title">Backups for: {{ client.name }}</h5>
							<small>All available backups </small>
						</div>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div class="row">
							<h4>Available Backups</h4>
							<div class="col-12" style="max-height: 349px;overflow: auto;">
								<table v-if="allBackups.length > 0" class="table table-hover">
									<tbody>
										<tr v-for="(backup, i) in allBackups" :key="i">
											<td class="align-middle" :title="backup.time">{{ backup.date }}</td>
											<td width="150">
												<div class="d-flex justify-content-between align-items-center">
													<p class="m-0 p-0 me-2">{{ backup.size }}</p>
													<button @click="applayBackup(backup, 'modal')" class="btn btn-primary btn-sm">
														<template v-if="backup.name === restore">
															<span style="margin-bottom: -3px;" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
														</template>
														<template v-else>Use</template>
													</button>
												</div>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
					</div>
				</div>
			</div>
		</div>
		<!-- #Modal for backups -->
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { Modal } from 'bootstrap'
import juniperApi from '@/api/juniperApi'
//import restApi from '../../../api/index'

export default {
	name: 'ClientStatic',
	data() {
		return {
			loading: false,
			client: {},
			zip: '',
			total_files: 0,
			total_directories: 0,
			total_size: 0,
			backups: [],
			allBackups: [],
			restore: '',
			$backupsModal: null }
	},
	components: {},
	computed: {
		...mapState(['authUser']),
		...mapGetters(['isAuth']) },
	mounted() {
		this.$backupsModal = new Modal('#backups')
	},
	created() {
		this.getClient()
	},
	methods: {
		// Get client
		getClient() {
			this.loading = true
			juniperApi
				.get('/clients/' + this.$route.params.id)
				.then(({ data }) => {
					this.client = data
					this.staticStructure()
					this.getBackups()
					this.loading = false
				})
				.catch(error => {
					this.loading = false
					console.log(error)
				})
		},

		// Download static
		async downloadStatic(backupUrl) {
			let filename = backupUrl.split('/').pop()
			try {
				const response = await juniperApi.post(
					'/clients/' + this.$route.params.id + '/static/download',
					{
						url: backupUrl },
					{
						responseType: 'blob' }
				)
				const url = window.URL.createObjectURL(new Blob([response.data]))
				const link = document.createElement('a')
				link.href = url
				link.setAttribute('download', filename)
				document.body.appendChild(link)
				link.click()
				link.remove()
			} catch (error) {
				console.error('Error downloading:', error)
				alert('Error downloading static files. Please try again later.')
			}
		},

		// Static structure
		staticStructure() {
			juniperApi
				.get('/clients/' + this.$route.params.id + '/static/structure')
				.then(({ data }) => {
					this.total_files = data.total_files
					this.total_directories = data.total_directories
					this.total_size = data.total_size
				})
				.catch(error => {
					console.log(error)
				})
		},

		// Get All Backups
		getAllBackups() {
			juniperApi
				.post('/clients/' + this.$route.params.id + '/static/backups/10000')
				.then(({ data }) => {
					this.allBackups = data
				})
				.catch(error => {
					console.log(error)
				})
		},

		// Get client Backups
		getBackups() {
			juniperApi
				.post('/clients/' + this.$route.params.id + '/static/backups/3')
				.then(({ data }) => {
					this.backups = data
				})
				.catch(error => {
					this.loading = false
					console.log(error)
				})
		},

		// Applay this backup
		applayBackup(backup, type) {
			this.$swal
				.fire({
					title: 'Are you sure want to restore website static from this backup?',
					text:
						'Notice that this not restoring data in database, its just a method to fast put site live if there is a problem. Static files from selected backup file will be uploaded on website.',
					icon: 'warning',
					position: 'center',
					showCancelButton: true,
					showConfirmButton: true,
					confirmButtonColor: '#3085d6',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Yes, apply this backup!',
					toast: false,
					timer: 0 })
				.then(result => {
					if (result.isConfirmed) {
						this.restore = backup.name
						juniperApi
							.post('/clients/' + this.$route.params.id + '/static/backup/apply', {
								path: backup.name })
							.then(({ data }) => {
								this.restore = ''
								if (type == 'modal') {
									this.$backupsModal.hide()
								}
								this.$swal.fire({
									icon: 'success',
									title: data.message,
									timer: 4000 })
							})
							.catch(error => {
								this.restore = ''
								this.loading = false
								console.log(error)
							})
					}
				})
		} } }
</script>
<style scoped>
#clientStatic {
	padding: 0 0 60px;
	margin-top: 0 !important;
}

.jc-view {
	width: 100%;
}

.js-stat {
	background: #fff;
	border: 1px solid #e5e7eb;
	border-radius: 12px;
	padding: 14px;
	height: 100%;
}

.js-stat__label {
	font-size: 12px;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	font-weight: 600;
}

.js-stat__value {
	font-size: 20px;
	font-weight: 700;
	color: #111827;
	line-height: 1.2;
	margin-top: 6px;
}

.js-stat__sub {
	font-size: 12px;
	color: #6b7280;
	margin-top: 2px;
}

.js-panel {
	background: #fff;
	border: 1px solid #e5e7eb;
	border-radius: 12px;
	padding: 14px;
}

.js-panel__title {
	font-size: 16px;
	font-weight: 700;
	color: #111827;
}

.js-panel__subtitle {
	font-size: 12px;
	color: #6b7280;
	margin-top: 2px;
}

.js-empty {
	margin-top: 12px;
	padding: 18px;
	border-radius: 10px;
	background: #f9fafb;
	border: 1px dashed #d1d5db;
	text-align: center;
	color: #6b7280;
}


@media (max-width: 768px) {
	.j-action-btn {
		min-width: unset;
	}
}
</style>
